import React from 'react';
import { SimulationConfig } from './types';
import { SectionLabel } from './SectionLabel';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { SETTING_INFO } from './settingInfo';

interface HUDSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const HUDSettingsSection: React.FC<HUDSettingsSectionProps> = ({ config, setConfig }) => {
  const updateConfig = (key: keyof SimulationConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 w-full p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 mb-2">
      <ControlGroup label="Show HUD" settingKey="showHUD" darkMode={config.darkMode}>
        <Toggle active={config.showHUD} onClick={() => updateConfig('showHUD', !config.showHUD)} />
      </ControlGroup>
      
      <ControlGroup label="Show Play/Reset UI" tooltip="Shows the floating play and restart buttons at the bottom of the screen." darkMode={config.darkMode}>
        <Toggle active={config.showControlIsland} onClick={() => updateConfig('showControlIsland', !config.showControlIsland)} />
      </ControlGroup>
      
      {config.showHUD && (
        <>
          <div className="flex flex-col gap-1 p-3 bg-black/20 rounded-xl border border-white/5">
            <ControlGroup label="Position" settingKey="hudPosition" darkMode={config.darkMode}>
              <select 
                value={config.hudPosition}
                onChange={(e) => updateConfig('hudPosition', e.target.value)}
                className={`border rounded-lg px-2 py-1.5 text-[10px] font-bold focus:outline-none focus:border-blue-500 ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
              >
                <option value="TOP_LEFT">Top Left</option>
                <option value="TOP_RIGHT">Top Right</option>
                <option value="BOTTOM_LEFT">Bottom Left</option>
                <option value="BOTTOM_RIGHT">Bottom Right</option>
              </select>
            </ControlGroup>
            <SmartInput 
              label="Opacity" 
              value={config.hudOpacity} 
              min={0} max={1} step={0.1} 
              onChange={(val) => updateConfig('hudOpacity', val)} 
              darkMode={config.darkMode}
              settingKey="hudOpacity"
            />
            <SmartInput 
              label="Scale" 
              value={config.hudScale} 
              min={0.5} max={2} step={0.1} 
              onChange={(val) => updateConfig('hudScale', val)} 
              darkMode={config.darkMode}
              settingKey="hudScale"
            />
          </div>

          <div className="flex flex-col gap-1 p-3 bg-black/20 rounded-xl border border-white/5">
            <div className="grid grid-cols-1 gap-1">
              {[
                { key: 'hudShowElapsedTime', label: 'Elapsed Time' },
                { key: 'hudShowSpeed', label: 'Speed' },
                { key: 'hudShowBallCount', label: 'Spheres' },
                { key: 'hudShowBounceCount', label: 'Bounces' },
                { key: 'hudShowRingsLeft', label: 'Rings Left', modes: ['ESCAPE', 'RHYTHMIC_ESCAPE'] },
                { key: 'hudShowTimeRemaining', label: 'Time Remaining', modes: ['ESCAPE', 'RHYTHMIC_ESCAPE'] },
                { key: 'hudShowFPS', label: 'FPS' },
                { key: 'hudShowCurrentRound', label: 'Current Round', mode: 'SPACE_SPIRAL' },
                { key: 'hudShowContainerRadius', label: 'Container Radius' },
                { key: 'hudShowBallRadius', label: 'Ball Radius' },
                { key: 'hudShowGravity', label: 'Gravity' },
                { key: 'hudShowRestitution', label: 'Restitution' },
              ].map((item: any) => {
                const itemModes = item.modes || (item.mode ? [item.mode] : undefined);
                const isVisible = !itemModes || itemModes.includes(config.mode);
                const disabledMsg = itemModes ? `This parameter is specific to ${itemModes.join(', ')} mode.` : undefined;
                
                return (
                  <ControlGroup 
                    key={item.key} 
                    label={item.label} 
                    settingKey={item.key}
                    disabled={!isVisible}
                    disabledMessage={disabledMsg}
                    modes={itemModes}
                    darkMode={config.darkMode}
                  >
                    <Toggle 
                      active={config[item.key as keyof SimulationConfig] as boolean} 
                      onClick={() => updateConfig(item.key as keyof SimulationConfig, !config[item.key as keyof SimulationConfig])} 
                      disabled={!isVisible}
                    />
                  </ControlGroup>
                );
              })}
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};
